import React, {useEffect, useState} from "react";
import {
    RateAudit,
    RateDeviation,
    SalaryIssue,
    SalaryRestControllerApiFactory,
} from "arteco-api-client-ts";
import {
    Box,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import TrendingDownRoundedIcon from "@mui/icons-material/TrendingDownRounded";
import SwapVertRoundedIcon from "@mui/icons-material/SwapVertRounded";
import ReportProblemRoundedIcon from "@mui/icons-material/ReportProblemRounded";
import EuroRoundedIcon from "@mui/icons-material/EuroRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import {useApiManager} from "../../../../components/utils/api";
import {fmtEur} from "../../../../components/utils/numbers";
import {
    EmptyState,
    FilterChips,
    ListCard,
    Page,
    PageHeader,
    Panel,
    PersonAvatar,
    SkeletonRows,
    SoftChip,
    StatGrid,
    StatTile,
    tableContainerSx,
    tableSx,
} from "../../../../components/kit";

const MESES = ["", "ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

const rate = (v?: number) => v === undefined || v === null ? "—"
    : `${v.toLocaleString("es-ES", {minimumFractionDigits: 2, maximumFractionDigits: 2})} €/h`;

const num = {fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap"} as const;

const ISSUE_LABELS: Record<string, string> = {
    DUPLICATE_START_DATE: "Fecha duplicada",
};

/** Impacto en euros con su sentido: de menos (rojo, se dejó de cobrar) o de más (ámbar). */
const Impact = ({value}: { value?: number }) => {
    const below = (value || 0) < 0;
    return (
        <Box>
            <Typography variant="body2" fontWeight={700} sx={{...num, color: below ? "error.main" : "warning.dark"}}>
                {fmtEur(value || 0)}
            </Typography>
            <Typography variant="caption" color="text.secondary">{below ? "de menos" : "de más"}</Typography>
        </Box>
    );
};

/**
 * Control anti-deriva de tarifas.
 *
 * Contrasta lo que se ha facturado (`Dedication.rate`) con lo que la persona tiene asignado
 * (`Salary.avgHourRate`). Las dos cifras viven en tablas distintas y nada obliga a que coincidan,
 * así que se separan sin que nadie vea un error.
 */
export const RateAuditPage = () => {
    const apiManager = useApiManager();
    const theme = useTheme();
    const compact = useMediaQuery(theme.breakpoints.down("md"));
    const [audit, setAudit] = useState<RateAudit | null>(null);
    const [loading, setLoading] = useState(true);
    const [months, setMonths] = useState(12);

    useEffect(() => {
        setLoading(true);
        const ctrl = apiManager.factory(SalaryRestControllerApiFactory);
        apiManager.execute(ctrl.rateAuditSalary(months))
            .then(r => setAudit(r as RateAudit))
            .finally(() => setLoading(false));
    }, [months]);

    const deviations: RateDeviation[] = audit?.deviations || [];
    const issues: SalaryIssue[] = audit?.issues || [];
    // Se facturó de menos: la tarifa subió en el maestro y se siguió cobrando la vieja.
    const belowTotal = deviations
        .filter(d => (d.impact || 0) < 0)
        .reduce((acc, d) => acc + Math.abs(d.impact || 0), 0);

    return (
        <Page>
            <PageHeader title="Control de tarifas"
                        subtitle="Meses en los que la tarifa facturada no coincide con la asignada a la persona."
                        actions={<FilterChips value={months} onChange={setMonths}
                                              options={[3, 6, 12, 24, 36].map(m => ({value: m, label: `${m} meses`}))}/>}/>

            {loading && <SkeletonRows rows={4} height={64}/>}

            {!loading && audit && (
                <>
                    <StatGrid>
                        <StatTile icon={<EuroRoundedIcon fontSize="small"/>}
                                  tone={(audit.totalImpact || 0) > 0 ? "warning" : "success"}
                                  label="Desviación total" value={fmtEur(audit.totalImpact || 0)}/>
                        <StatTile icon={<TrendingDownRoundedIcon fontSize="small"/>} tone={belowTotal > 0 ? "error" : "neutral"}
                                  label="Facturado de menos" value={fmtEur(belowTotal)}/>
                        <StatTile icon={<SwapVertRoundedIcon fontSize="small"/>} tone={deviations.length ? "warning" : "neutral"}
                                  label="Desviaciones" value={deviations.length}/>
                        <StatTile icon={<ReportProblemRoundedIcon fontSize="small"/>} tone={issues.length ? "warning" : "neutral"}
                                  label="Anomalías del maestro" value={issues.length}/>
                    </StatGrid>

                    {deviations.length === 0 && issues.length === 0 && (
                        <Panel>
                            <EmptyState icon={<VerifiedRoundedIcon/>} title="Ninguna desviación"
                                        description={`En los últimos ${months} meses todo lo facturado coincide con la tarifa asignada.`}/>
                        </Panel>
                    )}

                    <Stack spacing={3}>
                        {deviations.length > 0 && (
                            <Panel title="Desviaciones" subtitle="Por persona y mes: tarifa facturada frente a la asignada y su efecto en euros.">
                                {compact ? (
                                    <Stack spacing={1.5}>
                                        {deviations.map((d, i) => (
                                            <ListCard key={i}>
                                                <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
                                                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{minWidth: 0}}>
                                                        <PersonAvatar name={d.personName}/>
                                                        <Box sx={{minWidth: 0}}>
                                                            <Typography variant="body2" fontWeight={600} noWrap>{d.personName}</Typography>
                                                            <Typography variant="caption" color="text.secondary">{MESES[d.month || 0]} {d.year}</Typography>
                                                        </Box>
                                                    </Stack>
                                                    <Box sx={{textAlign: "right"}}><Impact value={d.impact}/></Box>
                                                </Stack>
                                                <Box sx={{display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, mt: 1.5}}>
                                                    <Box><Typography variant="caption" color="text.secondary" display="block">Facturado</Typography><Typography variant="body2" sx={num}>{rate(d.billedRate)}</Typography></Box>
                                                    <Box><Typography variant="caption" color="text.secondary" display="block">Asignado</Typography><Typography variant="body2" sx={num}>{rate(d.assignedRate)}</Typography></Box>
                                                    <Box><Typography variant="caption" color="text.secondary" display="block">Horas</Typography><Typography variant="body2" sx={num}>{(d.hours || 0).toLocaleString("es-ES", {minimumFractionDigits: 1, maximumFractionDigits: 1})}</Typography></Box>
                                                </Box>
                                            </ListCard>
                                        ))}
                                    </Stack>
                                ) : (
                                    <TableContainer sx={tableContainerSx}>
                                        <Table size="small" sx={tableSx}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Persona</TableCell>
                                                    <TableCell>Periodo</TableCell>
                                                    <TableCell align="right">Facturado</TableCell>
                                                    <TableCell align="right">Asignado</TableCell>
                                                    <TableCell align="right">Horas</TableCell>
                                                    <TableCell align="right">Impacto</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {deviations.map((d, i) => (
                                                    <TableRow key={i} hover>
                                                        <TableCell>
                                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                                <PersonAvatar name={d.personName}/>
                                                                <Typography variant="body2" fontWeight={600}>{d.personName}</Typography>
                                                            </Stack>
                                                        </TableCell>
                                                        <TableCell>{MESES[d.month || 0]} {d.year}</TableCell>
                                                        <TableCell align="right" sx={num}>{rate(d.billedRate)}</TableCell>
                                                        <TableCell align="right" sx={num}>{rate(d.assignedRate)}</TableCell>
                                                        <TableCell align="right" sx={num}>
                                                            {(d.hours || 0).toLocaleString("es-ES", {minimumFractionDigits: 1, maximumFractionDigits: 1})}
                                                        </TableCell>
                                                        <TableCell align="right"><Impact value={d.impact}/></TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </Panel>
                        )}

                        {issues.length > 0 && (
                            <Panel title="Anomalías del maestro de salarios"
                                   subtitle="Impiden saber cuál es la tarifa vigente, así que cualquier cálculo automático puede coger la equivocada.">
                                {compact ? (
                                    <Stack spacing={1.5}>
                                        {issues.map((it, i) => (
                                            <ListCard key={i}>
                                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                                    <Typography variant="body2" fontWeight={600}>{it.personName}</Typography>
                                                    <SoftChip tone="warning" label={ISSUE_LABELS[it.type || ""] || "Sin salario"}/>
                                                </Stack>
                                                <Typography variant="caption" color="text.secondary" display="block" sx={{mt: 0.5}}>{it.date}</Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>{it.detail}</Typography>
                                            </ListCard>
                                        ))}
                                    </Stack>
                                ) : (
                                    <TableContainer sx={tableContainerSx}>
                                        <Table size="small" sx={tableSx}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Persona</TableCell>
                                                    <TableCell>Tipo</TableCell>
                                                    <TableCell>Fecha</TableCell>
                                                    <TableCell>Detalle</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {issues.map((it, i) => (
                                                    <TableRow key={i} hover>
                                                        <TableCell><Typography variant="body2" fontWeight={600}>{it.personName}</Typography></TableCell>
                                                        <TableCell><SoftChip tone="warning" label={ISSUE_LABELS[it.type || ""] || "Sin salario"}/></TableCell>
                                                        <TableCell sx={num}>{it.date}</TableCell>
                                                        <TableCell><Typography variant="body2" color="text.secondary">{it.detail}</Typography></TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </Panel>
                        )}
                    </Stack>
                </>
            )}
        </Page>
    );
};
